import React, { useState, useEffect, useMemo } from 'react';
import WelcomeBanner from './WelcomeBanner';
import CustomerServicePopup from './CustomerServicePopup';
import VIPUpgradePopup from './VIPUpgradePopup';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useAuth } from '../hooks/useAuth';
import { getUserOptimizationStatus } from '../services/optimizationService';
import { checkVIPUpgradeEligibility, hasSeenUpgradePopupToday } from '../services/vipUpgradeService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

function Dashboard({ userProfile, username, vipLevel = 'VIP1', onStartOptimization, onOpenVIPLevels }) {
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const { user } = useAuth();
  const [isOptimizationLocked, setIsOptimizationLocked] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showCustomerService, setShowCustomerService] = useState(false);
  const [showVIPUpgrade, setShowVIPUpgrade] = useState(false);
  const [vipEligibility, setVipEligibility] = useState(null);
  
  // Check for daily reset on mount
  useEffect(() => {
    const checkDailyReset = async () => {
      if (!user?.uid || isAdmin) return;
      
      try {
        const { checkAndResetUserProgress } = await import('../services/dailyResetService');
        await checkAndResetUserProgress(user.uid);
      } catch (error) {
        console.error('Error checking daily reset:', error);
      }
    };
    
    checkDailyReset();
  }, [user?.uid, isAdmin]);

  // Real-time listener for user profile to detect balance changes and VIP upgrade eligibility
  useEffect(() => {
    if (!user?.uid || isAdmin) return;
    
    const checkVIPUpgrade = async (userData) => {
      try {
        const eligibility = await checkVIPUpgradeEligibility(user.uid);
        
        if (eligibility.eligible) {
          // Check if user has seen this popup today
          const targetVIP = eligibility.nextVIP.replace('VIP', '');
          const hasSeen = await hasSeenUpgradePopupToday(user.uid, targetVIP);
          
          if (!hasSeen) {
            setVipEligibility(eligibility);
            setShowVIPUpgrade(true);
          }
        } else {
          setShowVIPUpgrade(false);
          setVipEligibility(null);
        }
      } catch (error) {
        console.error('Error checking VIP upgrade:', error);
      }
    };
    
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = { id: docSnapshot.id, ...docSnapshot.data() };
        console.log('📊 User profile updated in Dashboard');
        
        // Check for VIP upgrade eligibility when balance changes
        await checkVIPUpgrade(userData);
      }
    }, (error) => {
      console.error('Error in user profile listener:', error);
    });
    
    // Initial check
    if (userProfile) {
      checkVIPUpgrade(userProfile);
    }
    
    return () => unsubscribe();
  }, [user?.uid, isAdmin, userProfile]);

  // Force re-render when userProfile changes
  useEffect(() => {
    // This effect ensures the component re-renders when userProfile changes
  }, [userProfile]);
  
  // Get balances from user profile or use defaults
  // Use memoization to ensure updates when userProfile changes
  // MUST be before any conditional returns (React Hooks rules)
  const balances = useMemo(() => {
    return userProfile?.balances || {
      total: 0,
      wallet: 0,
      frozen: 0,
      commission: 0,
      todaysProfit: 0,
      salary: 0,
      negative: 0
    };
  }, [userProfile?.balances]);

  // Display balances
  // If user has negative balance (knife applied), wallet shows negative amount
  const hasKnifeApplied = userProfile?.hasNegativeBalance || userProfile?.isKnifeApplied || false;
  const negativeAmount = balances.negativeAmount || (balances.negative || 0);
  
  // When knife is applied, show negative amounts
  const displayBalance = hasKnifeApplied && balances.wallet < 0 
    ? balances.wallet 
    : (balances.wallet || 0);
  const frozenBalance = (balances.frozen || 0);
  const totalBalance = hasKnifeApplied && balances.total < 0 
    ? balances.total 
    : (balances.total || 0);
  
  const displayBalanceStr = displayBalance.toFixed(2);
  const frozenBalanceStr = frozenBalance.toFixed(2);
  const totalBalanceStr = totalBalance.toFixed(2);

  const balanceCards = useMemo(() => [
    {
      id: 'total-balance',
      icon: 'bi-wallet2',
      iconBg: 'black',
      title: 'Total Balance',
      subtitle: hasKnifeApplied ? 'Account Balance' : 'Total Balance',
      value: totalBalanceStr,
      currency: 'USDT',
      isNegative: hasKnifeApplied && totalBalance < 0
    },
    {
      id: 'wallet-balance',
      icon: 'bi-wallet2',
      iconBg: 'black',
      title: 'Wallet Balance',
      subtitle: hasKnifeApplied ? 'Negative amount to deposit' : 'Profits will be added here.',
      value: displayBalanceStr,
      currency: 'USDT',
      isNegative: hasKnifeApplied && displayBalance < 0
    },
    {
      id: 'frozen-balance',
      icon: 'bi-wallet2',
      iconBg: 'black',
      title: 'Frozen Balance',
      subtitle: hasKnifeApplied ? 'Original balance before knife' : '',
      value: frozenBalanceStr,
      currency: 'USDT',
      isNegative: false
    },
    {
      id: 'todays-profit',
      icon: 'bi-currency-dollar',
      iconBg: 'blue',
      title: "Today's Profit",
      subtitle: 'Gain Profit',
      value: balances.todaysProfit?.toFixed(2) || '0.00',
      currency: 'USDT',
      isNegative: false
    },
    {
      id: 'salary',
      icon: 'bi-currency-dollar',
      iconBg: 'blue',
      title: 'Salary',
      subtitle: "Today's Salary",
      value: balances.salary?.toFixed(2) || '0.00',
      currency: 'USDT',
      isNegative: false
    }
  ], [displayBalanceStr, frozenBalanceStr, totalBalanceStr, balances.todaysProfit, balances.salary, hasKnifeApplied]);

  // Load optimization lock status (must be after all hooks)
  useEffect(() => {
    const loadLockStatus = async () => {
      if (!user?.uid || isAdmin) {
        setLoadingStatus(false);
        return;
      }
      
      try {
        const status = await getUserOptimizationStatus(user.uid);
        setIsOptimizationLocked(status.isLocked);
      } catch (error) {
        console.error('Error loading optimization status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };

    loadLockStatus();

    // Set up real-time listener
    if (user?.uid && !isAdmin) {
      const statusRef = doc(db, 'optimizationStatus', user.uid);
      const unsubscribe = onSnapshot(statusRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setIsOptimizationLocked(data.isLocked || false);
        }
      });

      return () => unsubscribe();
    }
  }, [user?.uid, isAdmin]);

  // Prevent admin users from viewing regular user dashboard
  if (!adminLoading && isAdmin) {
    return (
      <div className="dashboard-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh', flexDirection: 'column' }}>
          <div className="alert alert-warning" style={{ maxWidth: '500px', margin: '20px' }}>
            <h4 className="alert-heading">
              <i className="bi bi-shield-exclamation me-2"></i>
              Admin Account
            </h4>
            <p>Admin accounts should access the system through the admin panel.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-content">
        <div className="container-fluid px-3">
          <div className="dashboard-welcome-banner-wrapper mb-3">
            <WelcomeBanner username={username} />
          </div>
          
          {balanceCards.map((card) => (
            <div key={card.id} className="balance-card">
              <div className="balance-card-inner">
                <div className="d-flex align-items-center flex-wrap">
                  <div className={`balance-icon ${card.iconBg} flex-shrink-0`}>
                    <i className={`bi ${card.icon}`}></i>
                  </div>
                  <div className="balance-info flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="balance-title">{card.title}</div>
                    <div className="balance-subtitle">{card.subtitle}</div>
                  </div>
                  <div className="balance-value flex-shrink-0">
                    <div 
                      className="balance-amount"
                      style={card.isNegative ? { color: '#ff4444', fontWeight: 'bold' } : {}}
                    >
                      {card.value}
                    </div>
                    <div className="balance-currency">{card.currency}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="start-optimization-btn-container mt-4 mb-3">
            <button 
              className="btn btn-start-optimization w-100"
              onClick={() => {
                if (onStartOptimization && !isOptimizationLocked) {
                  onStartOptimization();
                }
              }}
              disabled={isOptimizationLocked || loadingStatus}
            >
              <i className="bi bi-play-circle-fill me-2"></i>
              {isOptimizationLocked 
                ? 'Optimization Locked - Contact Customer Service' 
                : 'Start Optimization'}
            </button>
            {isOptimizationLocked && (
              <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                <i className="bi bi-info-circle me-2"></i>
                Please contact customer service to unlock this feature.
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Customer Service Popup */}
      {showCustomerService && (
        <CustomerServicePopup onClose={() => setShowCustomerService(false)} />
      )}

      {/* VIP Upgrade Popup */}
      {showVIPUpgrade && vipEligibility && (
        <VIPUpgradePopup
          isOpen={showVIPUpgrade}
          onClose={() => setShowVIPUpgrade(false)}
          userId={user?.uid}
          eligibility={vipEligibility}
          onOpenVIPLevels={onOpenVIPLevels}
          onUpgradeSuccess={(result) => {
            console.log('VIP upgrade successful:', result);
            setShowVIPUpgrade(false);
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;

